import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Play, ArrowRight, Trophy, Gamepad2, Flame, Crown, GitBranch, Target, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Icon from "@/components/Icon";
import { catColor } from "@/lib/colors";

const LessonTypeBadge = ({ type }) => {
  const map = { quick_read: "Quick Read", quiz: "Quiz", flashcards: "Flashcards", checklist: "Checklist", decision_tree: "Decision Tree" };
  return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-muted-foreground">{map[type] || type}</span>;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.user_id) return;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem("lp_streak_reminder") === today) return;
    const t = setTimeout(() => {
      localStorage.setItem("lp_streak_reminder", today);
      if (user.streak > 0) {
        toast(`🔥 ${user.streak}-day streak!`, { description: "Complete today's mission to keep it alive.", duration: 5000 });
      } else {
        toast("👋 Welcome back!", { description: "Do one lesson today to start a streak.", duration: 5000 });
      }
    }, 900);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  if (!data) return <div className="grid place-items-center py-32"><Loader2 className="animate-spin text-indigo-400" size={32} /></div>;

  const lp = data.level_progress;
  const pct = Math.min(100, Math.round(((lp.xp - lp.xp_floor) / (lp.xp_next - lp.xp_floor)) * 100));
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-8">
      {/* Greeting + XP */}
      <div>
        <p className="text-muted-foreground text-sm">{greet},</p>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{user?.name} 👋</h1>
        <div className="mt-5 glass rounded-3xl p-5">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 grid place-items-center text-white font-display font-bold text-sm">{lp.level}</div>
              <span className="font-semibold text-sm">Level {lp.level} <span className="text-muted-foreground font-normal">· Life Pilot</span></span>
            </div>
            <span className="text-xs text-muted-foreground font-semibold">{lp.xp} / {lp.xp_next} XP</span>
          </div>
          <div className="h-3 rounded-full bg-white/5 overflow-hidden">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-400 shadow-[0_0_12px_rgba(129,140,248,0.6)]" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
          </div>
        </div>
      </div>

      {/* Daily mission */}
      {data.daily_mission && (
        <div>
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Target size={18} className="text-indigo-400" /> Daily Mission</h2>
          <motion.button data-testid="daily-mission-card" whileHover={{ y: -4 }} onClick={() => navigate(`/lesson/${data.daily_mission.id}`)}
            className="w-full text-left relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-indigo-600 to-violet-700 shadow-xl shadow-indigo-600/25">
            <div className="absolute -right-8 -bottom-8 opacity-20"><Icon name="target" size={140} className="text-white" /></div>
            <div className="relative z-10">
              <span className="text-[10px] uppercase tracking-widest font-bold text-white/70">Today's mission</span>
              <h3 className="font-display font-extrabold text-2xl text-white mt-1 mb-1">{data.daily_mission.title}</h3>
              <p className="text-white/80 text-sm mb-4">Earn {data.daily_mission.xp} XP · {data.daily_mission.coins} coins</p>
              <span className="inline-flex items-center gap-2 bg-white text-indigo-700 font-bold text-sm px-4 py-2 rounded-full">Start <Play size={15} fill="currentColor" /></span>
            </div>
          </motion.button>
        </div>
      )}

      {/* Quick tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { to: "/app/skill-tree", icon: GitBranch, label: "Skill Tree", c: "text-violet-400", bg: "bg-violet-500/10", b: "border-violet-500/25" },
          { to: "/app/challenges", icon: Trophy, label: "Challenges", c: "text-yellow-400", bg: "bg-yellow-500/10", b: "border-yellow-500/25" },
          { to: "/app/progress", icon: Flame, label: "Progress", c: "text-orange-400", bg: "bg-orange-500/10", b: "border-orange-500/25" },
          { to: "/app/premium", icon: Crown, label: "Premium", c: "text-emerald-400", bg: "bg-emerald-500/10", b: "border-emerald-500/25" },
        ].map((t) => (
          <motion.button key={t.to} whileHover={{ y: -3 }} data-testid={`tile-${t.label.toLowerCase().replace(" ", "-")}`} onClick={() => navigate(t.to)} className={`rounded-2xl p-4 border ${t.bg} ${t.b} flex flex-col items-center gap-2 transition-all`}>
            <t.icon className={t.c} size={26} />
            <span className="text-xs font-semibold">{t.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Life Simulations */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><Gamepad2 size={18} className="text-cyan-400" /> Life Simulations</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {data.simulations.map((s) => {
            const col = catColor(s.category_key === "career" ? "cyan" : "emerald");
            return (
              <motion.button key={s.key} whileHover={{ y: -4 }} data-testid={`sim-card-${s.key}`} onClick={() => navigate(`/simulation/${s.key}`)}
                className={`text-left rounded-3xl p-6 glass border ${col.border} relative overflow-hidden`}>
                <div className={`absolute -right-6 -top-6 h-28 w-28 rounded-full bg-gradient-to-br ${col.grad} opacity-20 blur-2xl`} />
                <span className={`text-[10px] uppercase tracking-widest font-bold ${col.text}`}>Simulation</span>
                <h3 className="font-display font-extrabold text-xl mt-1 mb-2">{s.title}</h3>
                <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{s.description}</p>
                <span className={`inline-flex items-center gap-1.5 text-sm font-bold ${col.text}`}>Play now <ArrowRight size={15} /></span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Worlds */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg">Learning Worlds</h2>
          <button data-testid="see-all-worlds" onClick={() => navigate("/app/worlds")} className="text-sm text-indigo-400 font-semibold flex items-center gap-1">All <ChevronRight size={15} /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {data.categories.slice(0, 5).map((c) => {
            const col = catColor(c.color);
            return (
              <motion.button key={c.key} whileHover={{ y: -4 }} data-testid={`world-${c.key}`} onClick={() => navigate(`/app/worlds/${c.key}`)}
                className={`text-left rounded-2xl p-4 glass border ${col.border}`}>
                <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${col.grad} grid place-items-center mb-3 shadow-lg`}>
                  <Icon name={c.icon} size={22} className="text-white" />
                </div>
                <h3 className="font-display font-bold text-sm">{c.name}</h3>
                <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className={`h-full rounded-full ${col.solid}`} style={{ width: `${c.progress}%` }} />
                </div>
                <span className="text-[11px] text-muted-foreground mt-1 block">{c.completed_count}/{c.lesson_count}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Recommended */}
      {data.recommended?.length > 0 && (
        <div>
          <h2 className="font-display font-bold text-lg mb-3">Recommended Lessons</h2>
          <div className="space-y-2.5">
            {data.recommended.map((l) => {
              const col = catColor(data.categories.find((c) => c.key === l.category_key)?.color);
              return (
                <motion.button key={l.id} whileHover={{ x: 4 }} data-testid={`rec-${l.id}`} onClick={() => navigate(`/lesson/${l.id}`)}
                  className="w-full flex items-center gap-3 glass rounded-2xl p-4 text-left">
                  <div className={`h-10 w-10 rounded-xl ${col.bg} grid place-items-center shrink-0`}><Icon name={data.categories.find((c) => c.key === l.category_key)?.icon} size={18} className={col.text} /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate">{l.title}</h3>
                    <div className="flex items-center gap-2 mt-1"><LessonTypeBadge type={l.type} /><span className="text-[11px] text-muted-foreground">+{l.xp} XP</span></div>
                  </div>
                  <ArrowRight size={18} className="text-muted-foreground shrink-0" />
                </motion.button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
